// src/component/ChangePasswordModal.js
import React, { useState } from 'react';
import { FaLock, FaSave, FaTimes, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient'; // Adjust path if needed

const ChangePasswordModal = ({ isOpen, onClose }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Reset form when modal opens or closes
    React.useEffect(() => {
        if (!isOpen) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setError(null);
            setIsSaving(false);
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null); // Clear previous errors

        // Frontend Validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError("All password fields are required.");
            return;
        }
        if (newPassword.length < 8) {
             setError("New password must be at least 8 characters.");
             return;
        }
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }
        if (currentPassword === newPassword) {
             setError("New password cannot be the same as the current password.");
             return;
        }


        setIsSaving(true);
        try {
            console.log("ChangePasswordModal: Submitting password change...");
            // Only send current and new password to backend
            await apiClient.put('/profile/password', { currentPassword, newPassword });

            toast.success("Password updated successfully!");
            onClose(); // Close modal on success

        } catch (err) {
            console.error("ChangePasswordModal: Error updating password:", err);
            const errorMsg = err.response?.data?.error || err.message || 'Failed to update password.';
            setError(errorMsg); // Show error inside the modal
            toast.error(`Password update failed: ${errorMsg}`);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    // --- Styles (inline for simplicity, reuse styles from Profile if needed) ---
    const modalStyles = styles.passwordModal; // Use styles object defined below
    const inputStyles = styles.input; // Reuse input style
    const buttonStyles = styles.actionButton; // Reuse action button style
    const saveButtonStyles = styles.saveButton;
    const cancelButtonStyles = styles.cancelButton;

    return (
        <div style={styles.modalOverlayStyle} onClick={onClose}>
            <div style={modalStyles.content} onClick={(e) => e.stopPropagation()}>
                <button style={modalStyles.closeButton} onClick={onClose} title="Close"> <FaTimes /> </button>
                <h2 style={modalStyles.title}><FaLock style={{marginRight: '10px'}}/> Change Password</h2>

                <form onSubmit={handleSubmit}>
                    <div style={modalStyles.fieldGroup}>
                        <label htmlFor="currentPassword" style={modalStyles.label}>Current Password:</label>
                        <input
                            type="password"
                            id="currentPassword"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            style={inputStyles}
                            required
                            disabled={isSaving}
                        />
                    </div>
                    <div style={modalStyles.fieldGroup}>
                        <label htmlFor="newPassword" style={modalStyles.label}>New Password:</label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            style={inputStyles}
                            required
                            minLength={8} // Add basic HTML validation
                            disabled={isSaving}
                        />
                    </div>
                    <div style={modalStyles.fieldGroup}>
                        <label htmlFor="confirmPassword" style={modalStyles.label}>Confirm New Password:</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            style={inputStyles}
                            required
                            minLength={8}
                            disabled={isSaving}
                        />
                    </div>

                    {error && (
                        <div style={styles.errorBox}><FaExclamationTriangle/> {error}</div>
                    )}

                    <div style={modalStyles.buttonGroup}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...buttonStyles, ...cancelButtonStyles }}
                            disabled={isSaving}
                        >
                             <FaTimes style={{marginRight: '5px'}}/> Cancel
                        </button>
                        <button
                            type="submit"
                            style={{ ...buttonStyles, ...saveButtonStyles }}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <><FaSpinner className="fa-spin" style={{marginRight: '5px'}}/> Updating...</>
                            ) : (
                                <><FaSave style={{marginRight: '5px'}}/> Update Password</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Reuse styles defined in Profile.js (or define separately) ---
// Assume 'styles' object contains necessary styles like:
// modalOverlayStyle, input, actionButton, saveButton, cancelButton, errorBox etc.
// Add specific styles for the password modal if needed
const styles = {
    // Assuming these are copied/imported from Profile.js styles
    modalOverlayStyle: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050, },
    input: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '1rem', boxSizing: 'border-box', marginBottom: '10px' }, // Added margin bottom
    actionButton: { padding: '10px 20px', borderRadius: '5px', border: 'none', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', },
    saveButton: { backgroundColor: '#1a73e8', color: '#fff', },
    cancelButton: { backgroundColor: '#f1f3f4', color: '#5f6368', border: '1px solid #dadce0', },
    errorBox: { color: 'red', backgroundColor: '#ffebee', border: '1px solid red', borderRadius: '4px', padding: '10px', marginTop: '0', marginBottom: '15px', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px', },
    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },

    // Specific Password Modal Styles (can merge/refine with other modal styles)
    passwordModal: {
        content: { backgroundColor: '#fff', padding: '30px 35px', borderRadius: '8px', maxWidth: '500px', width: '90%', position: 'relative', boxShadow: '0 5px 15px rgba(0,0,0,0.2)', },
        closeButton: { position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#888', },
        title: { marginTop: 0, marginBottom: '25px', color: '#1a237e', borderBottom: '1px solid #eee', paddingBottom: '15px', fontSize: '1.5rem', display: 'flex', alignItems: 'center' },
        fieldGroup: { marginBottom: '20px', }, // Increase spacing
        label: { fontWeight: '600', color: '#555', display: 'block', marginBottom: '8px', fontSize: '0.95rem', },
        buttonGroup: { display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '25px', },
    },
};

export default ChangePasswordModal;